<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";
import { resolveResourcePath } from "#bb/lib/pathContract";

const logger = useLogger("RankedReport");
const props = defineProps<{ id: string }>();
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const features = inject("render-features", {
  editor: false,
  resourceDefinitionPath: null as string | null,
});
// Inject report BEFORE building template context to avoid temporal dead zone ReferenceError
type InjectedReport = { data: any[]; kind?: string; loading: boolean; initialized: boolean; context?: any };
const report = inject<InjectedReport | null>("report", null);

// Build a reactive template context only when initialized
const templateContext = computed(() => buildTemplateContext(report));
// Re-create templating helpers reactively when context becomes available
const templating = computed(() => useTemplating(templateContext, { logger }));
const templatedIndividualPath = computed(() => {
  const applyTemplate = templating.value.applyTemplate;
  return applyTemplate(definitionData.value?.individualReportPath || definitionData.value?.individualReportPathRaw || "");
});

const commands = [
  {
    id: "editors-group",
    name: "Report",
    description: "Configure this report",
    type: "group",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Number of columns",
        action: async () => {
          logger.info("Edit command triggered for report", props.id);
          logger.debug("Emitting event for onEdit with report data", {
            data: definitionData.value,
          });
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Ranked report",
    icon: "lucide:file-chart-column-increasing",
  },
});

// Store + Provider
const { reportData, loading: storeLoading, fetchReports } = useReportsStore();
// (moved up) report injection now occurs before templateContext usage

// Spinner: use provider's loading when present; otherwise fall back to store
const isLoading = computed(() => (report ? (!report.initialized || report.loading) : storeLoading.value));

const rankedUsers = ref<any[]>([]);

// Compute an overall percentage from either pre‑calculated fields or an average of constructs
function computeOverall(user: any): number {
  if (!user) return 0;

  // Prefer pre-calculated fields; tolerate either 0..1 or 0..100
  for (const val of [user.overall, user.overallScore, user.score]) {
    const n = Number(val);
    if (!Number.isNaN(n)) return n > 1 ? n : n * 100;
  }

  const constructs: any[] = Array.isArray(user.constructs) ? user.constructs : [];
  if (!constructs.length) return 0;

  const selectedIds: string[] = definitionData.value?.selectedConstructIds ?? [];
  let relevant: any[] = selectedIds.length
    ? constructs.filter((c: any) => selectedIds.includes(String(c.id)))
    : constructs;

  // OPTIONAL: skip any overall buckets when averaging
  // relevant = relevant.filter(c => !String(c.name ?? '').toUpperCase().startsWith('OVERALL'));

  if (!relevant.length) return 0;
  const sum = relevant.reduce((acc: number, c: any) => acc + Number(c.score ?? 0), 0);
  return (sum / relevant.length) * 100;
}

// Build the ranked array from provider data (if it looks like users) or from the store
function computeRanking(): void {
  // If we are under a provider, respect its lifecycle and avoid store fallback
  if (report) {
    if (!report.initialized || report.loading) {
      rankedUsers.value = [];
      return;
    }

    const providerItems: any[] = Array.isArray(report.data) ? report.data : [];
    const providerIsUsers =
      report.kind === "users" ||
      providerItems.some((i: any) => i?.userId || i?.emailAddress); // fallback check

    if (providerIsUsers) {
      console.log("Provider data sample:", providerItems[0]); // Debug log
      rankedUsers.value = providerItems
        .map((item: any) => {
          const mapped = {
            ...item,
            name: String(item.name ?? "").replace(/&/g, " and "),
            emailAddress: item.emailAddress,
            overall: Math.round(computeOverall(item)),
          };
          console.log("Mapped item:", { name: mapped.name, emailAddress: mapped.emailAddress }); // Debug log
          return mapped;
        })
        .sort((a: any, b: any) => b.overall - a.overall);
      return;
    }
    // If provider present but not users, show empty by default
    rankedUsers.value = [];
    return;
  }

  // No provider: use the store's list of users
  const users: any[] = Array.isArray(reportData.value) ? reportData.value : [];
  rankedUsers.value = users
    .map((user: any) => ({
      name: String(user.name ?? "").replace(/&/g, " and "),
      emailAddress: user.emailAddress,
      userId: user.userId,
      overall: Math.round(computeOverall(user)),
    }))
    .sort((a: any, b: any) => b.overall - a.overall);
}

onMounted(async () => {
  if (!report) {
    await fetchReports();
  }
  computeRanking();
});

if (report) {
  watch(() => report.data, computeRanking, { deep: true });
  watch(() => report.loading, computeRanking);
  watch(() => report.initialized, computeRanking);
} else {
  watch(reportData, computeRanking);
  const route = useRoute();
  watch(
    () => route.query.user,
    async (newUser) => {
      await fetchReports(newUser as string | undefined);
      computeRanking();
    }
  );
}

// Optional: re-compute when selectedConstructIds change in the definition
watch(() => definitionData.value?.selectedConstructIds, computeRanking, { deep: true });

// --- Navigation ---
const { resolveHref } = useAppRoute();

function navigateToUser(user: any) {
  const path = templatedIndividualPath.value;
  if (path) {
    const resolvedPath = resolveResourcePath(
      path,
      features.resourceDefinitionPath,
    );
    navigateTo(`${resolveHref(resolvedPath)}?user=${user.userId}`);
  }
}
</script>

<template>
  <table class="w-full text-left table-auto">
    <thead>
      <tr class="border-b">
        <th class="px-4 py-2 w-11">Rank</th>
        <th class="px-4 py-2 w-72">Name</th>
        <th class="px-4 py-2">Email</th>
        <th class="px-4 py-2 w-40 text-right">Overall Score</th>
      </tr>
    </thead>
    <tbody v-if="isLoading">
      <tr
        v-for="n in 10"
        :key="`loading-${n}`"
        class="border-b cursor-pointer"
      >
        <td class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td class="px-4 py-2 font-bold text-right"><Skeleton class="h-6 w-full" /></td>
      </tr>
    </tbody>
    <tbody v-else>
      <tr
        v-for="(user, index) in rankedUsers"
        :key="user?.userId ?? user?.emailAddress ?? user?.name ?? index"
        class="border-b cursor-pointer"
        @click="navigateToUser(user)"
      >
        <td class="px-4 py-2">{{ index + 1 }}</td>
        <td class="px-4 py-2">{{ user.name }}</td>
        <td class="px-4 py-2">{{ user.emailAddress }} [DEBUG: {{ JSON.stringify(user.emailAddress) }}]</td>
        <td class="px-4 py-2 font-bold text-right">{{ user.overall }}%</td>
      </tr>
    </tbody>
  </table>

  {{ reportData }}<br/>
  {{ rankedUsers }}<br/>
  {{ report }}

  <div v-if="features.editor" class="hidden">
    <editor-reports-ranked :id="props.id" />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.RANKED_REPORT,
  icon: "lucide:file-chart-column-increasing",
  type: "Ranked Report",
  description: "A ranked list of candidates.",
  default: {},
  categories: ["Reports"],
  order: 12004,
};
</script>
